import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Mail, Building2, Handshake, Globe2, Users2, MessageSquare, ArrowLeft } from 'lucide-react';
import Modal from '../components/Modal';

export default function ContactPage() {
  const { theme } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    type: '',
    message: ''
  });

  const contactTypes = [
    {
      icon: <Building2 className="h-6 w-6 text-blue-500" />,
      title: 'Investment Opportunities',
      description: 'Partner with us to transform the global tech workforce',
      value: 'investment'
    },
    {
      icon: <Handshake className="h-6 w-6 text-green-500" />,
      title: 'Strategic Partnership',
      description: 'Collaborate on workforce development initiatives',
      value: 'partnership'
    },
    {
      icon: <Globe2 className="h-6 w-6 text-purple-500" />,
      title: 'Regional Expansion',
      description: 'Bring Gocity Group to your region',
      value: 'expansion'
    },
    {
      icon: <Users2 className="h-6 w-6 text-red-500" />,
      title: 'Training Programs',
      description: 'Implement workforce training solutions',
      value: 'training'
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    setIsModalOpen(false);
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Return to About Link */}
        <Link
          to="/about"
          className={`
            inline-flex items-center space-x-2 mb-8 group
            ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}
          `}
        >
          <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          <span>Return to About Us</span>
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-4">
            <MessageSquare className="h-12 w-12 text-blue-500" />
          </div>
          <h1 className={`text-4xl font-bold mb-4 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Get in Touch
          </h1>
          <p className={`text-xl max-w-3xl mx-auto ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Connect with us to explore how we can collaborate on transforming the global technology workforce through strategic investments in developing nations.
          </p>
        </div>

        {/* Contact Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactTypes.map((type) => (
            <div
              key={type.value}
              onClick={() => {
                setFormData(prev => ({ ...prev, type: type.value }));
                setIsModalOpen(true);
              }}
              className={`
                p-6 rounded-lg cursor-pointer transition-all duration-200
                ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                ${formData.type === type.value
                  ? theme === 'dark'
                    ? 'ring-2 ring-blue-500'
                    : 'ring-2 ring-blue-600'
                  : ''
                }
                hover:scale-105 hover:shadow-lg
              `}
            >
              <div className="flex justify-center mb-4">
                {type.icon}
              </div>
              <h3 className={`text-lg font-bold text-center mb-2 ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {type.title}
              </h3>
              <p className={`text-sm text-center ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {type.description}
              </p>
              <button
                className={`
                  w-full mt-4 px-4 py-2 rounded-lg transition-colors
                  ${theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                Contact Us
              </button>
            </div>
          ))}
        </div>

        {/* Global Offices */}
        <div className="mt-16">
          <h2 className={`text-2xl font-bold text-center mb-8 ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Our Global Presence
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                city: 'Venice',
                country: 'Italy',
                address: 'EMILIA, 17',
                type: 'Headquarters'
              },
              {
                city: 'Uyo',
                country: 'Nigeria',
                address: 'Uyo',
                type: 'Regional workspace'
              },
              {
                city: 'Calabar',
                country: 'Nigeria',
                address: 'Calabar',
                type: 'Regional workspace'
              }
            ].map((office) => (
              <div
                key={office.city}
                className={`
                  p-6 rounded-lg
                  ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
                `}
              >
                <div className={`text-sm font-medium mb-1 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`}>
                  {office.type}
                </div>
                <h3 className={`text-lg font-bold mb-2 ${
                  theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                }`}>
                  {office.city}, {office.country}
                </h3>
                <p className={`text-sm ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {office.address}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form Modal */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Contact Us"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Your Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={`
                    w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                    ${theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }
                  `}
                  required
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Organization
              </label>
              <input
                type="text"
                value={formData.organization}
                onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
                className={`
                  w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
                required
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Message
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                rows={6}
                className={`
                  w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                  ${theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                  }
                `}
                required
              />
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className={`
                  px-4 py-2 rounded-lg transition-colors
                  ${theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Send Message
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
}